import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, mergeProps as _mergeProps, renderList as _renderList, Fragment as _Fragment } from "vue";
const _hoisted_1 = { class: "recap__head" };
const _hoisted_2 = ["aria-expanded"];
const _hoisted_3 = { class: "recap__label" };
const _hoisted_4 = {
    key: 0,
    class: "recap__summary"
};
const _hoisted_5 = {
    key: 0,
    class: "recap__actions"
};
const _hoisted_6 = ["aria-pressed", "disabled"];
const _hoisted_7 = ["disabled", "aria-label"];
const _hoisted_8 = {
    class: "recap__sr",
    role: "status"
};
const _hoisted_9 = {
    key: 0,
    id: "pa-recap-body",
    class: "recap__body"
};
const _hoisted_10 = { class: "recap__sections" };
const _hoisted_11 = {
    key: 1,
    class: "recap__empty"
};
const _hoisted_12 = {
    key: 2,
    class: "recap__loading",
    "aria-hidden": "true"
};
const _hoisted_13 = { class: "recap__sections" };
import { computed, onBeforeUnmount, ref, toRef } from "vue";
import PaRecapText from "@/components/paWorkspace/PaRecapText.vue";
import { useOrderRecap } from "@/composables/useOrderRecap";
import { recapLines } from "@/utils/recapSources";
import { timeAgoString } from "@/utils";
export default /*@__PURE__*/ _defineComponent({
    __name: 'PaRecap',
    props: {
        orderKey: {},
        open: { type: Boolean, default: true }
    },
    emits: ["update:open", "open-document", "reveal-question"],
    setup(__props, { emit: __emit }) {
        const REASONS = [
            { value: "wrong", label: "Something is wrong" },
            { value: "missing", label: "Missing something" },
            { value: "not_useful", label: "Not useful" },
            { value: "too_long", label: "Too long" },
        ];
        const ROW_TITLES = ["Blocker", "Next step"];
        const props = __props;
        const emit = __emit;
        const { recap, unavailable, slow, rating, refresh, rate } = useOrderRecap(toRef(props, "orderKey"));
        const now = ref(Date.now());
        const ticker = setInterval(() => (now.value = Date.now()), 30_000);
        onBeforeUnmount(() => clearInterval(ticker));
        const sections = computed(() => recap.value?.sections ?? null);
        const rows = computed(() => (sections.value ?? []).filter((section) => section.key !== "summary"));
        const lines = computed(() => recapLines(sections.value ?? [], recap.value?.citations ?? []));
        const generating = computed(() => recap.value?.status === "generating" && !slow.value);
        const failed = computed(() => recap.value?.status === "failed");
        const failedEmpty = computed(() => (failed.value || slow.value) && !sections.value);
        const dimmed = computed(() => Boolean(sections.value && (recap.value?.stale || generating.value)));
        const canRate = computed(() => !rating.value && !dimmed.value);
        const summaryEmpty = computed(() => {
            const summary = sections.value?.find((section) => section.key === "summary");
            return Boolean(summary && isEmpty(summary));
        });
        const generatedAgo = computed(() => {
            if (!recap.value?.generatedAt)
                return "";
            const at = new Date(recap.value.generatedAt);
            return now.value - at.getTime() < 60_000 ? "just now" : `${timeAgoString(at, new Date(now.value))} ago`;
        });
        const stamp = computed(() => {
            if (!recap.value)
                return slow.value ? "Unavailable" : "Loading";
            if (slow.value)
                return sections.value ? `Update is slow · ${generatedAgo.value}` : "Taking longer than usual";
            if (generating.value)
                return sections.value ? "Updating" : "Writing";
            if (failed.value)
                return sections.value ? `Could not update · ${generatedAgo.value}` : "Unavailable";
            if (recap.value.stale)
                return `Out of date · ${generatedAgo.value}`;
            return `Updated ${generatedAgo.value}`;
        });
        const stampTone = computed(() => {
            if (generating.value)
                return "busy";
            if (failed.value || slow.value || recap.value?.stale)
                return "warn";
            return "quiet";
        });
        const announcement = computed(() => {
            if (!recap.value)
                return slow.value ? "The AI recap could not be loaded" : "";
            if (generating.value)
                return sections.value ? "Updating the AI recap" : "Writing the AI recap";
            if (failed.value || slow.value)
                return "The AI recap could not be updated";
            return sections.value ? "AI recap ready" : "";
        });
        const headline = computed(() => {
            for (const key of ["next_step", "summary"]) {
                const section = sections.value?.find((item) => item.key === key);
                if (section && !isEmpty(section))
                    return (lines.value[key] ?? []).map((line) => line.text).join(" ");
            }
            return "";
        });
        function isEmpty(section) {
            return !section.sentences.some((sentence) => sentence.sourceIds.length);
        }
        function follow(source) {
            if (source.target === "question")
                emit("reveal-question", source.ref);
            else if (source.target === "document")
                emit("open-document", source.ref);
        }
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_list_subheader = _resolveComponent("v-list-subheader");
            const _component_v_list_item = _resolveComponent("v-list-item");
            const _component_v_list = _resolveComponent("v-list");
            const _component_v_menu = _resolveComponent("v-menu");
            return (!_unref(unavailable))
                ? (_openBlock(), _createElementBlock("section", {
                    key: 0,
                    class: _normalizeClass(["recap", { 'recap--open': __props.open, 'recap--dim': dimmed.value }]),
                    "aria-label": "AI recap"
                }, [
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("button", {
                            class: "recap__toggle",
                            "aria-expanded": __props.open,
                            "aria-controls": "pa-recap-body",
                            onClick: _cache[0] || (_cache[0] = ($event) => (emit('update:open', !__props.open)))
                        }, [
                            _createElementVNode("span", _hoisted_3, [
                                _createVNode(_component_v_icon, {
                                    size: "13",
                                    class: "recap__spark"
                                }, {
                                    default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                            _createTextVNode("mdi-creation-outline", -1)
                                        ]))]),
                                    _: 1
                                }),
                                _cache[4] || (_cache[4] = _createTextVNode(" AI recap ", -1))
                            ]),
                            (!__props.open && headline.value)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_4, _toDisplayString(headline.value), 1))
                                : _createCommentVNode("", true),
                            _createElementVNode("span", {
                                class: _normalizeClass(["recap__stamp", `recap__stamp--${stampTone.value}`])
                            }, _toDisplayString(stamp.value), 3),
                            _createVNode(_component_v_icon, {
                                size: "18",
                                class: "recap__chev"
                            }, {
                                default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                                        _createTextVNode("mdi-chevron-down", -1)
                                    ]))]),
                                _: 1
                            })
                        ], 8, _hoisted_2),
                        (sections.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                _createElementVNode("button", {
                                    class: "recap__icon",
                                    "aria-pressed": _unref(recap)?.rating === 'up',
                                    disabled: !canRate.value,
                                    "aria-label": "Helpful recap",
                                    title: "Helpful",
                                    onClick: _cache[1] || (_cache[1] = ($event) => (_unref(rate)('up')))
                                }, [
                                    _createVNode(_component_v_icon, { size: "15" }, {
                                        default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(_unref(recap)?.rating === "up" ? "mdi-thumb-up" : "mdi-thumb-up-outline"), 1)
                                        ]),
                                        _: 1
                                    })
                                ], 8, _hoisted_6),
                                _createVNode(_component_v_menu, {
                                    location: "bottom end",
                                    offset: "4",
                                    disabled: !canRate.value
                                }, {
                                    activator: _withCtx(({ props: menuProps }) => [
                                        _createElementVNode("button", _mergeProps(menuProps, {
                                            class: "recap__icon",
                                            disabled: !canRate.value,
                                            "aria-label": _unref(recap)?.rating === 'down' ? 'Recap needs work, selected' : 'Recap needs work',
                                            title: "Needs work"
                                        }), [
                                            _createVNode(_component_v_icon, { size: "15" }, {
                                                default: _withCtx(() => [
                                                    _createTextVNode(_toDisplayString(_unref(recap)?.rating === "down" ? "mdi-thumb-down" : "mdi-thumb-down-outline"), 1)
                                                ]),
                                                _: 1
                                            })
                                        ], 16, _hoisted_7)
                                    ]),
                                    default: _withCtx(() => [
                                        _createVNode(_component_v_list, {
                                            class: "recap__reasons",
                                            density: "compact",
                                            color: "primary",
                                            role: "menu",
                                            "aria-label": "What is off?"
                                        }, {
                                            default: _withCtx(() => [
                                                _createVNode(_component_v_list_subheader, { class: "recap__reasons-label" }, {
                                                    default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                                            _createTextVNode("What is off?", -1)
                                                        ]))]),
                                                    _: 1
                                                }),
                                                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(REASONS, (option) => {
                                                    return _createVNode(_component_v_list_item, {
                                                        key: option.value,
                                                        class: "recap__reason",
                                                        role: "menuitem",
                                                        active: _unref(recap)?.rating === 'down' && _unref(recap)?.reason === option.value,
                                                        title: option.label,
                                                        onClick: ($event) => (_unref(rate)('down', option.value))
                                                    }, null, 8, ["active", "title", "onClick"]);
                                                }), 64))
                                            ]),
                                            _: 1
                                        })
                                    ]),
                                    _: 1
                                }, 8, ["disabled"])
                            ]))
                            : _createCommentVNode("", true)
                    ]),
                    _createElementVNode("span", _hoisted_8, _toDisplayString(announcement.value), 1),
                    (__props.open)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                            (sections.value)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    (lines.value.summary?.length)
                                        ? (_openBlock(), _createElementBlock("p", {
                                            key: 0,
                                            class: _normalizeClass(["recap__lead", { 'recap__lead--empty': summaryEmpty.value }])
                                        }, [
                                            _createVNode(PaRecapText, {
                                                lines: lines.value.summary,
                                                onFollow: follow
                                            }, null, 8, ["lines"])
                                        ], 2))
                                        : _createCommentVNode("", true),
                                    _createElementVNode("dl", _hoisted_10, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (section) => {
                                            return (_openBlock(), _createElementBlock(_Fragment, {
                                                key: section.key
                                            }, [
                                                _createElementVNode("dt", null, _toDisplayString(section.title), 1),
                                                _createElementVNode("dd", {
                                                    class: _normalizeClass({ 'recap__dd--empty': isEmpty(section), 'recap__dd--next': section.key === 'next_step' })
                                                }, [
                                                    _createVNode(PaRecapText, {
                                                        lines: lines.value[section.key] ?? [],
                                                        onFollow: follow
                                                    }, null, 8, ["lines"])
                                                ], 2)
                                            ], 64));
                                        }), 128))
                                    ])
                                ], 64))
                                : (failedEmpty.value)
                                    ? (_openBlock(), _createElementBlock("p", _hoisted_11, [
                                        _createTextVNode(" The recap could not be " + _toDisplayString(_unref(recap) ? "written" : "loaded") + " right now. ", 1),
                                        _createElementVNode("button", {
                                            class: "recap__retry",
                                            onClick: _cache[2] || (_cache[2] =
                                                //@ts-ignore
                                                (...args) => (_unref(refresh) && _unref(refresh)(...args)))
                                        }, "Try again")
                                    ]))
                                    : (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                        _cache[8] || (_cache[8] = _createElementVNode("span", { class: "recap__bar recap__bar--lead" }, null, -1)),
                                        _createElementVNode("dl", _hoisted_13, [
                                            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(ROW_TITLES, (title) => {
                                                return (_openBlock(), _createElementBlock(_Fragment, { key: title }, [
                                                    _createElementVNode("dt", null, _toDisplayString(title), 1),
                                                    _cache[7] || (_cache[7] = _createElementVNode("dd", null, [
                                                        _createElementVNode("span", { class: "recap__bar" })
                                                    ], -1))
                                                ], 64));
                                            }), 64))
                                        ])
                                    ]))
                        ]))
                        : _createCommentVNode("", true)
                ], 2))
                : _createCommentVNode("", true);
        };
    }
});
